import { bandStripGeometry, type BandStripGeometry, type BandStripSegment, type QuizBandRange } from '@/utils/quizBands'

/** Shades by rank, lowest band lightest. */
const BAND_TONES = [
    'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
    'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200',
    'bg-slate-300 text-slate-900 dark:bg-slate-600 dark:text-white',
    'bg-slate-400 text-slate-900 dark:bg-slate-500 dark:text-white',
    'bg-slate-500 text-white dark:bg-slate-400 dark:text-slate-900',
]

const OVERLAP_HATCH = 'repeating-linear-gradient(135deg, #fbbf24 0 3px, #fef3c7 3px 7px)'

const TICK_ALIGN = { start: '', center: '-translate-x-1/2', end: '-translate-x-full' } as const

function Segment({ segment }: { segment: BandStripSegment }) {
    const width = `${segment.widthPercent.toFixed(3)}%`
    if (segment.kind === 'overlap') {
        const title = segment.bandIndices.length === 2 ? 'Claimed by two bands' : `Claimed by ${segment.bandIndices.length} bands`
        return <div style={{ width, backgroundImage: OVERLAP_HATCH }} title={title} />
    }
    if (segment.kind === 'gap') return <div style={{ width }} className="rounded-sm border border-dashed border-slate-300 dark:border-slate-600" title="Not in any band" />
    return <div style={{ width }} className={`${BAND_TONES[Math.min(segment.rank, BAND_TONES.length - 1)]} flex items-center px-3 truncate`}>{segment.label}</div>
}

/** The bands laid along 0 to 100. Pass `geometry` when the caller already computed it. */
export function QuizBandStrip({ bands, geometry }: { bands: QuizBandRange[]; geometry?: BandStripGeometry }) {
    const { segments, ticks } = geometry ?? bandStripGeometry(bands)
    if (!segments.some(segment => segment.kind === 'band')) return null
    return (
        <div>
            <div className="flex h-9 rounded-md overflow-hidden text-xs font-medium gap-px bg-white dark:bg-slate-800">
                {segments.map(segment => <Segment key={`${segment.kind}-${segment.from}`} segment={segment} />)}
            </div>
            <div className="relative mt-1.5 h-4 text-[11px] tabular-nums">
                {ticks.map(tick => (
                    <span
                        key={`${tick.value}-${tick.leftPercent}`}
                        style={{ left: `${tick.leftPercent.toFixed(3)}%` }}
                        className={`absolute ${TICK_ALIGN[tick.align]} ${tick.isOverlap ? 'text-amber-700 font-medium dark:text-amber-300' : 'text-slate-500 dark:text-slate-400'}`}
                    >{tick.value}</span>
                ))}
            </div>
        </div>
    )
}
